import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveShow, userDisplayName } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import CompositeJudgeMapDialog from "./CompositeJudgeMapDialog";

// Card #1742 — "Composite judges" tab on the Judges page. Lists the show's
// roster and, for each judge, whether it's a plain single row or a composite
// row mapped to a primary + secondary pair (card #1342's mapping entity).
// A mapping may be created/edited/removed at any time, including after the
// composite judge has scored sheets or a bound signature (#1342 AC7 — the
// write path never touches the roster row or the signature pointer), so
// there is deliberately no UI-side block on a signed/scored judge here.
//
// Reads/writes go through the generated score-common client only
// (scoreApi.judges.*CompositeJudgeMap*) — never a direct fetch, never a
// second resolver. `judges` is the SAME list JudgeManager already fetched
// for the roster tab (score-api's roster is the only source of who a
// mapping may target — #1342 AC6, no minting), passed down rather than
// re-fetched here.
//
// Write-action gating: the create/update/delete routes require
// score:manage_judges server-side (already granted to the score_admin role,
// #1342). scoreadmin-fe has no per-component permission check anywhere
// today (grepped) — the ENTIRE admin shell is already gated on the
// score_admin role by ScoreEntitlementGate (App.jsx), which is how
// JudgeManager's existing admin action (signature capture, JudgeDrawer) is
// gated: nobody without score_admin reaches this component at all. This
// panel follows that same posture rather than adding a second, competing
// permission-check pattern that doesn't exist elsewhere in this app.
export default function CompositeJudgeMapPanel({ judges, activeShowId }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  // #2195 — activeShowId comes in as a prop (JudgeManager's own useActiveShow
  // read), but that prop alone can't distinguish "no show yet" from "the
  // show-list fetch just hasn't resolved" (activeShowId is null either way).
  // Reading the same shared context here for its `loading` flag is cheap
  // (ActiveShowContext dedupes across consumers) and keeps this panel correct
  // even if a future caller passes it a still-loading activeShowId directly.
  const { loading: loadingShow } = useActiveShow();

  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dialogJudge, setDialogJudge] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  const loadMaps = useCallback(async () => {
    if (!activeShowId) {
      setMaps([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const list = unwrap(await scoreApi.judges.listCompositeJudgeMaps(activeShowId));
      setMaps(list);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [scoreApi, activeShowId]);

  useEffect(() => {
    loadMaps();
  }, [loadMaps]);

  const judgesById = useMemo(() => {
    const byId = new Map();
    judges.forEach((j) => byId.set(j.id, j));
    return byId;
  }, [judges]);

  const mapsByCompositeId = useMemo(() => {
    const byId = new Map();
    maps.forEach((m) => byId.set(m.compositeJudgeId, m));
    return byId;
  }, [maps]);

  const sortedJudges = useMemo(
    () => [...judges].sort((a, b) => userDisplayName(a).localeCompare(userDisplayName(b))),
    [judges],
  );

  const closeMapDialog = () => setDialogJudge(null);

  const handleSaved = () => {
    closeMapDialog();
    loadMaps();
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    try {
      setRemoving(true);
      setRemoveError(null);
      unwrap(await scoreApi.judges.deleteCompositeJudgeMap(removeTarget.id));
      setRemoveTarget(null);
      loadMaps();
    } catch (err) {
      setRemoveError({ message: err.message || t("judges.compositeTab.removeError") });
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("judges.compositeTab.intro")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || t("judges.loadError")}
        </Alert>
      )}

      {!activeShowId ? (
        loadingShow ? (
          <TableSkeleton rows={4} columns={2} />
        ) : (
          <Typography color="text.secondary">{t("judges.noShow")}</Typography>
        )
      ) : loading ? (
        <TableSkeleton rows={4} columns={2} />
      ) : sortedJudges.length === 0 ? (
        <Typography color="text.secondary">{t("judges.empty")}</Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("judges.compositeTab.columns.name")}</TableCell>
                <TableCell>{t("judges.compositeTab.columns.mapping")}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedJudges.map((judge) => {
                const mapping = mapsByCompositeId.get(judge.id) || null;
                const primary = mapping ? judgesById.get(mapping.primaryJudgeId) : null;
                const secondary = mapping ? judgesById.get(mapping.secondaryJudgeId) : null;

                return (
                  <TableRow key={judge.id}>
                    <TableCell>{userDisplayName(judge) || "-"}</TableCell>
                    <TableCell>
                      {mapping ? (
                        t("judges.compositeTab.mappedTo", {
                          primary: primary ? userDisplayName(primary) : "-",
                          secondary: secondary ? userDisplayName(secondary) : "-",
                        })
                      ) : (
                        <Typography component="span" color="text.secondary">
                          {t("judges.compositeTab.unmapped")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => setDialogJudge(judge)}
                        sx={{
                          color: colors.foxOrange,
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        {mapping
                          ? t("judges.compositeTab.editButton")
                          : t("judges.compositeTab.mapButton")}
                      </Button>
                      {mapping && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            setRemoveError(null);
                            setRemoveTarget(judge);
                          }}
                          sx={{ ml: 1 }}
                        >
                          {t("judges.compositeTab.removeButton")}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CompositeJudgeMapDialog
        open={!!dialogJudge}
        onClose={closeMapDialog}
        activeShowId={activeShowId}
        compositeJudge={dialogJudge}
        existingMapping={dialogJudge ? mapsByCompositeId.get(dialogJudge.id) || null : null}
        judgeOptions={dialogJudge ? judges.filter((j) => j.id !== dialogJudge.id) : []}
        onSaved={handleSaved}
      />

      <Dialog
        open={!!removeTarget}
        onClose={() => (removing ? null : setRemoveTarget(null))}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
      >
        <DialogTitle color="text.primary">{t("judges.compositeTab.removeConfirmTitle")}</DialogTitle>
        <DialogContent>
          {removeError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {removeError.message}
            </Alert>
          )}
          <Typography color="text.secondary">
            {t("judges.compositeTab.removeConfirmBody", {
              name: removeTarget ? userDisplayName(removeTarget) : "",
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setRemoveTarget(null)}
            disabled={removing}
            sx={{ color: "text.primary" }}
          >
            {t("judges.compositeTab.removeConfirmCancel")}
          </Button>
          <Button color="error" variant="contained" onClick={confirmRemove} disabled={removing}>
            {removing
              ? t("judges.compositeTab.removing")
              : t("judges.compositeTab.removeConfirmButton")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
